/**
 * ExpertProfileDetailPage – Ansicht fuer ein einzelnes Experten-Profil.
 * Zeigt das Profil eines anderen Users an (read-only fuer normale User).
 * User mit edit-all Berechtigung oder PLATFORM_ADMIN koennen bearbeiten.
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { usePermissions } from '../auth/usePermissions';
import * as epApi from './api';
import type { ExpertProfile } from './types';
import { UserAvatar } from '../components/UserAvatar';
import { ExpertProfileTab } from './ExpertProfileTab';
import api from '../api/client'; // fuer Export-Downloads
import styles from './ExpertProfileDetailPage.module.css';

interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  jobTitle?: string | null;
  department?: string | null;
  avatar?: string | null;
  companyName?: string | null;
}

export function ExpertProfileDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const [editMode, setEditMode] = useState(false);

  const isPlatformAdmin = currentUser?.role === 'PLATFORM_ADMIN';
  const canEditAll = isPlatformAdmin || hasPermission('expert-profile.edit-all');

  // Profil laden (enthaelt bereits User-Daten vom Backend)
  const { data: profileData, isLoading: profileLoading, refetch } = useQuery<ExpertProfile & { user?: UserInfo | null }>({
    queryKey: ['expert-profile', userId],
    queryFn: () => epApi.getUserProfile(userId!),
    enabled: !!userId,
  });

  const profile = profileData;
  const userInfo = profileData?.user ?? undefined;

  const handleExport = async (format: 'pdf' | 'docx') => {
    if (!userId) return;
    try {
      const url =
        format === 'pdf'
          ? epApi.getUserExportPdfUrl(userId)
          : epApi.getUserExportDocxUrl(userId);
      const response = await api.get(url, { responseType: 'blob' });
      const mimeType = format === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const blob = new Blob([response.data], { type: mimeType });
      const disposition = response.headers['content-disposition'] as string | undefined;
      const utf8Match = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
      const plainMatch = disposition?.match(/filename="([^"]+)"/i);
      const filename = utf8Match
        ? decodeURIComponent(utf8Match[1])
        : plainMatch?.[1] ?? `Expertenprofil.${format}`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      // Export fehlgeschlagen
    }
  };

  const months = [
    'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
    'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez',
  ];

  if (profileLoading) {
    return (
      <div className={styles.page}>
        <p className={styles.loading}>Profil wird geladen...</p>
      </div>
    );
  }

  // Bearbeitungsmodus: ExpertProfileTab mit Admin-API-Base
  if (editMode && userId && canEditAll) {
    return (
      <div className={styles.page}>
        <div className={styles.topBar}>
          <button
            className={styles.backBtn}
            onClick={() => {
              setEditMode(false);
              refetch();
            }}
          >
            ← Zurück zur Ansicht
          </button>
          <div className={styles.exportBar}>
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', alignSelf: 'center' }}>
              Bearbeite Profil von {userInfo?.firstName} {userInfo?.lastName}
            </span>
          </div>
        </div>
        <ExpertProfileTab apiBase={`/expert-profile/users/${userId}`} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={() => navigate('/expert-profile')}>
          ← Zurück
        </button>
        <div className={styles.exportBar}>
          {canEditAll && (
            <button className={styles.editBtn} onClick={() => setEditMode(true)}>
              Bearbeiten
            </button>
          )}
          <button className={styles.exportBtn} onClick={() => handleExport('pdf')}>
            PDF
          </button>
          <button className={styles.exportBtn} onClick={() => handleExport('docx')}>
            Word
          </button>
        </div>
      </div>

      {/* Profil-Header */}
      <div className={styles.profileHeader}>
        <UserAvatar
          firstName={userInfo?.firstName ?? '?'}
          lastName={userInfo?.lastName ?? ''}
          avatar={userInfo?.avatar}
          size={72}
        />
        <div className={styles.profileInfo}>
          <h1 className={styles.profileName}>
            {userInfo?.firstName} {userInfo?.lastName}
          </h1>
          {userInfo?.jobTitle && (
            <span className={styles.profileJob}>{userInfo.jobTitle}</span>
          )}
          {userInfo?.department && (
            <span className={styles.profileDept}>{userInfo.department}</span>
          )}
          {userInfo?.companyName && (
            <span className={styles.profileCompany}>{userInfo.companyName}</span>
          )}
        </div>
      </div>

      {/* Skills */}
      {profile && profile.skills.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Skills &amp; Kompetenzen</h2>
          <div className={styles.chipList}>
            {profile.skills.map((s) => {
              const name = typeof s === 'string' ? s : s.name;
              return <span key={name} className={styles.chip}>{name}</span>;
            })}
          </div>
        </div>
      )}

      {/* Erfahrungsbereiche */}
      {profile && profile.experiences.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Erfahrungsbereiche</h2>
          <div className={styles.expGrid}>
            {profile.experiences.map((exp) => (
              <div key={exp.id} className={styles.expItem}>
                <strong>{exp.area}</strong>
                <div className={styles.expMeta}>
                  <span className={styles.badge}>{exp.years} Jahre</span>
                  {exp.level && <span className={styles.badgeSec}>{exp.level}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sprachen */}
      {profile && profile.languages.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Sprachen</h2>
          <div className={styles.langGrid}>
            {profile.languages.map((lang) => (
              <div key={lang.id} className={styles.langItem}>
                <span className={styles.langName}>{lang.language}</span>
                <span className={styles.badge}>{lang.level}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Projekte */}
      {profile && profile.projects.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Projekte / Berufserfahrung</h2>
          {profile.projects
            .sort((a, b) => b.fromYear - a.fromYear || b.fromMonth - a.fromMonth)
            .map((proj) => (
              <div key={proj.id} className={styles.projectCard}>
                <div className={styles.projectHeader}>
                  <div>
                    <strong>{proj.role}</strong>
                    {proj.company && (
                      <span className={styles.projectCompany}> bei {proj.company}</span>
                    )}
                  </div>
                  <span className={styles.projectDate}>
                    {months[proj.fromMonth - 1]} {proj.fromYear}
                    {' – '}
                    {proj.isCurrent
                      ? 'heute'
                      : `${months[(proj.toMonth ?? 1) - 1]} ${proj.toYear}`}
                  </span>
                </div>
                {proj.industry && (
                  <div className={styles.projectMeta}>
                    Branche: {proj.industry}
                    {proj.companySize && ` | Groesse: ${proj.companySize}`}
                  </div>
                )}
                {proj.tasks && <p className={styles.projectTasks}>{proj.tasks}</p>}
              </div>
            ))}
        </div>
      )}

      {/* Zertifikate */}
      {profile && profile.certifications.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Zertifikate &amp; Weiterbildungen</h2>
          {profile.certifications
            .sort((a, b) => b.issueYear - a.issueYear)
            .map((cert) => (
              <div key={cert.id} className={styles.certItem}>
                <div>
                  <strong>{cert.title}</strong>
                  <span className={styles.certIssuer}>{cert.issuingBody}</span>
                </div>
                <div className={styles.certRight}>
                  <span className={styles.badgeSec}>{cert.issueYear}</span>
                  {cert.website && (
                    <a
                      href={cert.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.link}
                    >
                      ↗
                    </a>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
